if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.unifiedGallery = Boolean(this.querySelector('.ohli-product-media-slider'));
        this.mobileMediaBufferSize = 2;
        this.primaryImage = this.elements.viewer?.querySelector('img[fetchpriority="high"]');
        this.initialMediaPending = Boolean(this.primaryImage && !this.primaryImage.complete);
        this.releaseInitialMedia = () => {
          if (!this.initialMediaPending) return;
          this.initialMediaPending = false;
          this.primaryImage?.removeEventListener('load', this.releaseInitialMedia);
          this.primaryImage?.removeEventListener('error', this.releaseInitialMedia);
          this.observeDeferredMedia();
          if (!this.mql.matches || this.unifiedGallery) this.primeAdjacentMedia();
        };
        this.deferredMediaSelector = 'img[data-ohli-pdp-media-deferred]';
        this.deferredMediaObserver =
          'IntersectionObserver' in window
            ? new IntersectionObserver(
                (entries) => {
                  entries.forEach((entry) => {
                    if (entry.isIntersecting) this.materializeMedia(entry.target);
                  });
                },
                { rootMargin: '400px 0px' }
              )
            : null;

        this.observeDeferredMedia();
        this.elements.viewer?.addEventListener('pointerdown', () => {
          this.releaseInitialMedia();
          this.primeAdjacentMedia();
        }, {
          passive: true,
        });
        this.elements.viewer?.addEventListener('click', this.onViewerClick.bind(this), true);
        if (this.unifiedGallery) {
          this.setupMobileSwipe();
          this.setupDesktopSwipe();
        }
        if (!this.mql.matches || this.unifiedGallery) this.primeAdjacentMedia();
        this.elements.viewer?.addEventListener('slideChanged', this.onSlideChanged.bind(this));
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const currentMedia = event.detail?.currentElement;
        if (!currentMedia) return;
        // Initialization/resize can announce the first slide without interaction.
        if (currentMedia !== this.primaryImage?.closest('[data-media-id]')) this.releaseInitialMedia();
        this.primeAdjacentMedia();
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentMedia.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      connectedCallback() {
        if (this.initialMediaPending) {
          this.primaryImage.addEventListener('load', this.releaseInitialMedia);
          this.primaryImage.addEventListener('error', this.releaseInitialMedia);
          if (this.primaryImage.complete) this.releaseInitialMedia();
        }
        this.observeDeferredMedia();
      }

      disconnectedCallback() {
        this.primaryImage?.removeEventListener('load', this.releaseInitialMedia);
        this.primaryImage?.removeEventListener('error', this.releaseInitialMedia);
        this.deferredMediaObserver?.disconnect();
      }

      setActiveMedia(mediaId, prepend) {
        this.releaseInitialMedia();
        this.observeDeferredMedia();
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.materializeMedia(activeMedia);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.primeAdjacentMedia(activeMedia);

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.unifiedGallery || !this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      materializeMedia(root) {
        if (!root) return;
        const images = [];
        if (root.matches?.(this.deferredMediaSelector)) images.push(root);
        root.querySelectorAll?.(this.deferredMediaSelector).forEach((image) => images.push(image));

        images.forEach((image) => {
          image.loading = 'eager';
          if (image.dataset.srcset) image.setAttribute('srcset', image.dataset.srcset);
          if (image.dataset.src) image.setAttribute('src', image.dataset.src);
          image.removeAttribute('data-srcset');
          image.removeAttribute('data-src');
          image.removeAttribute('data-ohli-pdp-media-deferred');
          image.removeAttribute('hidden');
        });

        const item = root.closest?.('[data-media-id]');
        if (item && !item.querySelector(this.deferredMediaSelector)) this.deferredMediaObserver?.unobserve(item);
      }

      observeDeferredMedia() {
        if (this.initialMediaPending) return;
        this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((item) => {
          if (!item.querySelector(this.deferredMediaSelector)) return;
          if (this.deferredMediaObserver) this.deferredMediaObserver.observe(item);
          else this.materializeMedia(item);
        });
      }

      primeAdjacentMedia(activeMedia) {
        if (this.initialMediaPending) return;
        const slider = this.elements.viewer?.slider || this.elements.viewer?.querySelector('[id^="Slider-"]');
        const items = Array.from(slider?.querySelectorAll(':scope > [data-media-id]') || [])
          .filter((item) => item.getClientRects().length);
        if (!items.length) return;
        const scrollLeft = slider.scrollLeft || 0;
        const current = activeMedia && items.includes(activeMedia) ? items.indexOf(activeMedia) : items.reduce((best, item, index) =>
          Math.abs(item.offsetLeft - scrollLeft) < Math.abs(items[best].offsetLeft - scrollLeft) ? index : best, 0);
        const mediaAhead = this.unifiedGallery || !this.mql.matches ? this.mobileMediaBufferSize : 1;
        for (let offset = -1; offset <= mediaAhead; offset += 1) {
          this.materializeMedia(items[current + offset]);
        }
      }

      onViewerClick(event) {
        const button = event.target.closest?.('button[name="previous"], button[name="next"]');
        if (!button) return;
        this.releaseInitialMedia();
        this.primeAdjacentMedia();
      }

      swipeStart(slider, x, y) {
        const items = Array.from(slider.querySelectorAll(':scope > .product__media-item'));
        if (items.length < 2) return null;
        const current = items.reduce((best, item, index) =>
          Math.abs(item.offsetLeft - slider.scrollLeft) < Math.abs(items[best].offsetLeft - slider.scrollLeft) ? index : best, 0);
        return { x, y, current, items, scrollLeft: slider.scrollLeft };
      }

      swipeTarget(start, delta) {
        const width = start.items[start.current].getBoundingClientRect().width;
        if (Math.abs(delta) < width / 3) return start.current;
        return Math.max(0, Math.min(start.items.length - 1, start.current + (delta < 0 ? 1 : -1)));
      }

      setupMobileSwipe() {
        const slider = this.elements.viewer?.slider;
        if (!slider) return;

        let gesture = null;
        slider.addEventListener('touchstart', (event) => {
          gesture = null;
          if (this.mql.matches || event.touches.length !== 1 || event.target.closest('.ohli-pdp-video-actions')) return;

          const touch = event.touches[0];
          const start = this.swipeStart(slider, touch.clientX, touch.clientY);
          if (start) gesture = { ...start, id: touch.identifier };
        }, { passive: true });

        slider.addEventListener('touchend', (event) => {
          if (!gesture || this.mql.matches) return;
          const start = gesture;
          gesture = null;
          const touch = Array.from(event.changedTouches).find((item) => item.identifier === start.id);
          if (!touch) return;

          const dx = touch.clientX - start.x;
          const dy = touch.clientY - start.y;
          if (Math.abs(dx) <= Math.abs(dy)) return;

          const next = this.swipeTarget(start, dx);
          if (next === start.current) return;
          // Aguarda o fim do gesto nativo; não intercepta o scroll vertical.
          requestAnimationFrame(() => slider.scrollTo({ left: start.items[next].offsetLeft, behavior: 'smooth' }));
        }, { passive: true });

        slider.addEventListener('touchcancel', () => { gesture = null; }, { passive: true });
      }

      setupDesktopSwipe() {
        const slider = this.elements.viewer?.slider;
        if (!slider) return;

        // O arrasto nativo da imagem gera um ghost image em vez de mover a galeria.
        slider.querySelectorAll('.product__media-item img').forEach((image) => { image.draggable = false; });

        let gesture = null;
        let suppressClick = false;
        slider.addEventListener('pointerdown', (event) => {
          gesture = null;
          if (!this.mql.matches || event.pointerType !== 'mouse' || event.button !== 0 ||
              event.target.closest('.ohli-pdp-video-actions')) return;
          const start = this.swipeStart(slider, event.clientX, event.clientY);
          if (start) gesture = { ...start, id: event.pointerId, dragging: false };
        });

        slider.addEventListener('pointermove', (event) => {
          if (!gesture || gesture.id !== event.pointerId) return;
          const dx = event.clientX - gesture.x;
          const dy = event.clientY - gesture.y;
          if (!gesture.dragging) {
            if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy)) return;
            gesture.dragging = true;
            gesture.snap = slider.style.scrollSnapType;
            gesture.behavior = slider.style.scrollBehavior;
            slider.style.scrollSnapType = 'none';
            slider.style.scrollBehavior = 'auto';
            slider.setPointerCapture(event.pointerId);
          }
          event.preventDefault();
          slider.scrollLeft = gesture.scrollLeft - dx;
        });

        const finish = (event, cancelled = false) => {
          if (!gesture || gesture.id !== event.pointerId) return;
          const start = gesture;
          gesture = null;
          if (!start.dragging) return;
          const dx = event.clientX - start.x;
          const next = cancelled ? start.current : this.swipeTarget(start, dx);
          if (slider.hasPointerCapture(event.pointerId)) slider.releasePointerCapture(event.pointerId);
          slider.style.scrollSnapType = start.snap;
          slider.style.scrollBehavior = start.behavior;
          suppressClick = true;
          setTimeout(() => { suppressClick = false; }, 0);
          requestAnimationFrame(() => slider.scrollTo({ left: start.items[next].offsetLeft, behavior: 'smooth' }));
        };
        slider.addEventListener('pointerup', (event) => finish(event));
        slider.addEventListener('pointercancel', (event) => finish(event, true));
        slider.addEventListener('click', (event) => {
          if (!suppressClick) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          suppressClick = false;
        }, true);

        // Trackpad envia wheel horizontal, não eventos de arrasto do mouse.
        let wheel = null;
        let wheelTimer;
        slider.addEventListener('wheel', (event) => {
          if (!this.mql.matches || event.deltaMode !== 0 || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
          if (!wheel) {
            const start = this.swipeStart(slider, 0, 0);
            if (!start) return;
            wheel = { ...start, delta: 0 };
          }
          wheel.delta += event.deltaX;
          clearTimeout(wheelTimer);
          wheelTimer = setTimeout(() => {
            const start = wheel;
            wheel = null;
            const next = this.swipeTarget(start, -start.delta);
            if (next !== start.current) slider.scrollTo({ left: start.items[next].offsetLeft, behavior: 'smooth' });
          }, 120);
        }, { passive: true });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
